import { normalizeLabel } from './normalizeLabel'

/**
 * Valores de `modifier_groups.display_style` — columna YA EXISTENTE desde
 * supabase/migrations/20260516100000_advanced_modifiers.sql, NOT NULL DEFAULT
 * 'normal'. NO se crea ninguna columna nueva para el flujo guiado: duplicarla
 * dejaría dos marcas de "esto es una remoción" que pueden contradecirse entre
 * la comanda de cocina (KDSOrderCard ya lee display_style) y la pantalla del
 * cliente.
 */
export type DisplayStyle = 'normal' | 'removal' | 'allergy_alert'

/** Dónde va una opción en la pantalla final de extras. */
export type OptionKind = 'add' | 'remove' | 'choice'

// Prefijos que delatan una remoción. El espacio/guion tras "no" y "wo" es
// intencional: sin él, "Nocilla" caería en el bloque Quitar.
const REMOVAL_PREFIXES = [
  'sin ',
  'sin-',
  'no ',
  'no-',
  'quitar',
  'sacar',
  'without ',
  'w/o',
  'wo ',
  '- ',
]

const REMOVAL_EXACT = ['sin', 'no']

/**
 * ¿La etiqueta suena a remoción? Se usa para las opciones de modificador (junto
 * con su precio) y también, sola, para las notas rápidas — que no tienen precio
 * ni grupo pero sí necesitan subir al bloque Quitar ("Sin cebolla").
 */
export function isRemovalLabel(name: string): boolean {
  const n = normalizeLabel(name)
  if (REMOVAL_EXACT.includes(n)) return true
  return REMOVAL_PREFIXES.some((p) => n.startsWith(p))
}

/**
 * Clasifica una opción para la pantalla final de extras.
 *
 * Orden de las reglas:
 *  1. `removal` → todo el grupo es Quitar. Es la marca explícita del dueño y
 *     gana sobre el nombre y sobre el precio.
 *  2. `allergy_alert` → el grupo no se disuelve; se muestra con su nombre.
 *  3. `normal` (o nulo) → heurística POR OPCIÓN. Hace falta que sea por opción
 *     porque hay grupos mixtos reales: DE HAMBURGUESAS Y PANES tiene 30
 *     opciones, 7 de quitar y 8 pagas.
 *
 * No existe marca de "todo este grupo es Agregar" y no hace falta: `add` ya es
 * el destino por defecto, y una opción que cuesta nunca cae en Quitar.
 */
export function classifyOption(
  group: { display_style?: DisplayStyle | string | null },
  option: { name: string; priceDelta: number }
): OptionKind {
  if (group.display_style === 'removal') return 'remove'
  if (group.display_style === 'allergy_alert') return 'choice'
  if (option.priceDelta > 0) return 'add'
  return isRemovalLabel(option.name) ? 'remove' : 'add'
}
